// Attorney Portal State Store
// BP-10-04: Attorney Frontend

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  CaseFilters,
  CaseStatus,
  SettlementCalculation,
  StructuredPayment,
} from '@/features/attorney/types/attorney';

// ============================================================================
// Case Filters Store
// ============================================================================

interface CaseFiltersState {
  filters: CaseFilters;
  setFilters: (filters: Partial<CaseFilters>) => void;
  clearFilters: () => void;
  toggleStatus: (status: CaseStatus) => void;
}

const defaultFilters: CaseFilters = {
  status: [],
  caseType: undefined,
  search: '',
  dateFrom: undefined,
  dateTo: undefined,
};

export const useCaseFiltersStore = create<CaseFiltersState>((set, get) => ({
  filters: defaultFilters,
  
  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    })),
  
  clearFilters: () => set({ filters: defaultFilters }),
  
  toggleStatus: (status) =>
    set((state) => {
      const currentStatuses = state.filters.status || [];
      const hasStatus = currentStatuses.includes(status);
      
      return {
        filters: {
          ...state.filters,
          status: hasStatus
            ? currentStatuses.filter((s) => s !== status)
            : [...currentStatuses, status],
        },
      };
    }),
}));

// ============================================================================
// Settlement Calculator Store
// ============================================================================

interface SettlementCalculatorState {
  // Inputs
  grossAmount: number;
  attorneyFeePercentage: number;
  litigationCosts: number;
  medicalLiens: number;
  otherDeductions: number;
  
  // Structured payments
  structuredPayments: StructuredPayment[];
  
  // Discount rate for present value
  discountRate: number;
  
  // Actions
  setGrossAmount: (amount: number) => void;
  setAttorneyFeePercentage: (percentage: number) => void;
  setLitigationCosts: (costs: number) => void;
  setMedicalLiens: (liens: number) => void;
  setOtherDeductions: (deductions: number) => void;
  setDiscountRate: (rate: number) => void;
  
  // Structured payment actions
  addStructuredPayment: (payment: Omit<StructuredPayment, 'id'>) => void;
  updateStructuredPayment: (id: string, payment: Partial<StructuredPayment>) => void;
  removeStructuredPayment: (id: string) => void;
  clearStructuredPayments: () => void;
  
  // Calculate results
  getCalculation: () => SettlementCalculation;
  
  // Reset
  reset: () => void;
}

const defaultCalculatorState = {
  grossAmount: 0,
  attorneyFeePercentage: 33.33,
  litigationCosts: 0,
  medicalLiens: 0,
  otherDeductions: 0,
  structuredPayments: [] as StructuredPayment[],
  discountRate: 3.5,
};

export const useSettlementCalculatorStore = create<SettlementCalculatorState>((set, get) => ({
  ...defaultCalculatorState,
  
  setGrossAmount: (amount) => set({ grossAmount: amount }),
  setAttorneyFeePercentage: (percentage) => set({ attorneyFeePercentage: percentage }),
  setLitigationCosts: (costs) => set({ litigationCosts: costs }),
  setMedicalLiens: (liens) => set({ medicalLiens: liens }),
  setOtherDeductions: (deductions) => set({ otherDeductions: deductions }),
  setDiscountRate: (rate) => set({ discountRate: rate }),
  
  addStructuredPayment: (payment) =>
    set((state) => ({
      structuredPayments: [
        ...state.structuredPayments,
        { ...payment, id: crypto.randomUUID() },
      ],
    })),
  
  updateStructuredPayment: (id, payment) =>
    set((state) => ({
      structuredPayments: state.structuredPayments.map((p) =>
        p.id === id ? { ...p, ...payment } : p
      ),
    })),
  
  removeStructuredPayment: (id) =>
    set((state) => ({
      structuredPayments: state.structuredPayments.filter((p) => p.id !== id),
    })),
  
  clearStructuredPayments: () => set({ structuredPayments: [] }),
  
  getCalculation: () => {
    const state = get();
    const attorneyFees = state.grossAmount * (state.attorneyFeePercentage / 100);
    const totalDeductions =
      attorneyFees +
      state.litigationCosts +
      state.medicalLiens +
      state.otherDeductions;
    const netToClient = state.grossAmount - totalDeductions;
    
    // Calculate structured total
    let structuredValue = 0;
    state.structuredPayments.forEach((payment) => {
      if (payment.paymentType === 'lump_sum') {
        structuredValue += payment.amount;
      } else if (payment.paymentType === 'periodic' && payment.startDate && payment.endDate) {
        const startDate = new Date(payment.startDate);
        const endDate = new Date(payment.endDate);
        const years = (endDate.getTime() - startDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
        
        let periodsPerYear = 1;
        switch (payment.frequency) {
          case 'monthly': periodsPerYear = 12; break;
          case 'quarterly': periodsPerYear = 4; break;
          case 'annually': periodsPerYear = 1; break;
        }
        
        const totalPeriods = Math.floor(years * periodsPerYear);
        let currentAmount = payment.amount;
        
        for (let i = 0; i < totalPeriods; i++) {
          structuredValue += currentAmount;
          if (payment.growthRate && i > 0 && i % periodsPerYear === 0) {
            currentAmount *= (1 + payment.growthRate / 100);
          }
        }
      } else {
        structuredValue += payment.amount;
      }
    });
    
    // Calculate present value discount
    const presentValueDiscount = structuredValue > 0
      ? structuredValue - calculatePresentValueTotal(state.structuredPayments, state.discountRate)
      : 0;
    
    return {
      grossSettlement: state.grossAmount,
      attorneyFees,
      attorneyFeePercentage: state.attorneyFeePercentage,
      litigationCosts: state.litigationCosts,
      medicalLiens: state.medicalLiens,
      otherDeductions: state.otherDeductions,
      netToClient,
      structuredValue,
      presentValueDiscount,
    };
  },
  
  reset: () => set(defaultCalculatorState),
}));

// Helper function for present value calculation
function calculatePresentValueTotal(
  payments: StructuredPayment[],
  discountRate: number
): number {
  const today = new Date();
  let presentValue = 0;
  
  payments.forEach((payment) => {
    if (payment.paymentType === 'lump_sum' && payment.startDate) {
      const paymentDate = new Date(payment.startDate);
      const years = (paymentDate.getTime() - today.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
      presentValue += payment.amount / Math.pow(1 + discountRate / 100, Math.max(0, years));
    }
    // Additional calculations for periodic payments would go here
  });
  
  return presentValue;
}

// ============================================================================
// Attorney UI State Store
// ============================================================================

interface AttorneyUIState {
  // Sidebar state
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  
  // Active case context
  activeCaseId: string | null;
  setActiveCaseId: (caseId: string | null) => void;
  
  // View preferences
  casesViewMode: 'grid' | 'list';
  setCasesViewMode: (mode: 'grid' | 'list') => void;
  
  // Timeline filters
  timelineEventTypes: string[];
  setTimelineEventTypes: (types: string[]) => void;
}

export const useAttorneyUIStore = create<AttorneyUIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      
      activeCaseId: null,
      setActiveCaseId: (caseId) => set({ activeCaseId: caseId }),
      
      casesViewMode: 'grid',
      setCasesViewMode: (mode) => set({ casesViewMode: mode }),
      
      timelineEventTypes: [],
      setTimelineEventTypes: (types) => set({ timelineEventTypes: types }),
    }),
    {
      name: 'attorney-ui-state',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        casesViewMode: state.casesViewMode,
      }),
    }
  )
);

// ============================================================================
// Life Care Plan Editor Store
// ============================================================================

interface LifeCarePlanEditorState {
  // Current editing context
  isEditing: boolean;
  unsavedChanges: boolean;
  
  // Actions
  startEditing: () => void;
  stopEditing: () => void;
  markUnsaved: () => void;
  markSaved: () => void;
  
  // Expand/collapse sections
  expandedSections: string[];
  toggleSection: (section: string) => void;
  expandAllSections: () => void;
  collapseAllSections: () => void;
}

export const useLifeCarePlanEditorStore = create<LifeCarePlanEditorState>((set) => ({
  isEditing: false,
  unsavedChanges: false,
  expandedSections: ['medical', 'therapy', 'equipment'],
  
  startEditing: () => set({ isEditing: true }),
  stopEditing: () => set({ isEditing: false, unsavedChanges: false }),
  markUnsaved: () => set({ unsavedChanges: true }),
  markSaved: () => set({ unsavedChanges: false }),
  
  toggleSection: (section) =>
    set((state) => ({
      expandedSections: state.expandedSections.includes(section)
        ? state.expandedSections.filter((s) => s !== section)
        : [...state.expandedSections, section],
    })),
  
  expandAllSections: () =>
    set({
      expandedSections: [
        'medical',
        'therapy',
        'equipment',
        'home',
        'care',
        'transportation',
        'education',
      ],
    }),
  
  collapseAllSections: () => set({ expandedSections: [] }),
}));

